import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import PropertyCard from "@/components/PropertyCard";
import { breadcrumbSchema } from "@/lib/schema";

export default function DeveloperDetail() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/developers/${slug}`).then((r) => setD(r.data)); }, [slug]);
  if (!d) return <div className="min-h-[60vh] flex items-center justify-center text-neutral-500">Loading…</div>;
  const crumbs = [{ name: "Home", path: "/" }, { name: "Developers", path: "/developers" }, { name: d.name, path: `/developers/${slug}` }];
  const description = d.description || d.bio;
  const founded = d.year_established || d.founded;
  return (
    <div data-testid={`dev-detail-${slug}`}>
      <SEO title={d.name} description={description?.slice(0, 160)} path={`/developers/${slug}`} image={d.hero_image || d.logo} schema={breadcrumbSchema(crumbs)} />
      {d.hero_image ? (
        <section className="relative min-h-[55vh] flex items-end overflow-hidden">
          <img src={d.hero_image} alt={d.name} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/75 to-black/25"></div>
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/55 to-transparent pointer-events-none"></div>
          <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 pb-16 w-full text-white" style={{ textShadow: "0 2px 16px rgba(0,0,0,0.55)" }}>
            <Breadcrumbs items={crumbs} />
            {founded && <div className="overline text-white/85 mb-4">Developer · Est. {founded}</div>}
            <h1 className="font-serif text-5xl lg:text-7xl leading-tight max-w-4xl text-white">{d.name}</h1>
          </div>
        </section>
      ) : (
        <section className="pt-8 pb-12 border-b border-neutral-200 bg-white">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <Breadcrumbs items={crumbs} />
            <div className="flex items-start gap-6 mt-6">
              {d.logo && (
                <div className="w-28 h-28 border border-neutral-200 bg-white flex items-center justify-center p-3 flex-shrink-0" data-testid="dev-logo">
                  <img src={d.logo} alt={d.name} className="max-w-full max-h-full object-contain" />
                </div>
              )}
              <div>
                {founded && <div className="overline text-[#001588] mb-3">Developer · Est. {founded}</div>}
                <h1 className="font-serif text-4xl lg:text-6xl leading-tight text-[#050d19]">{d.name}</h1>
                <div className="text-sm text-neutral-500 mt-2">
                  {d.emirate}{d.headquarters ? ` · ${d.headquarters}` : ""}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
      <section className="py-14 border-b border-neutral-200">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-12 grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            {description && <p className="text-neutral-700 leading-relaxed text-lg whitespace-pre-line">{description}</p>}

            {/* Editorial sections — only render blocks the admin has populated */}
            {(d.strengths?.length > 0) && (
              <div className="mt-12" data-testid="dev-strengths">
                <h2 className="font-serif text-3xl lg:text-4xl text-[#050d19] mb-5">Strengths</h2>
                <ul className="list-disc pl-6 space-y-2 text-neutral-700 text-base leading-relaxed marker:text-[#001588]">
                  {d.strengths.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
            {(d.signature_properties?.length > 0) && (
              <div className="mt-12" data-testid="dev-signature">
                <h2 className="font-serif text-3xl lg:text-4xl text-[#050d19] mb-5">Signature properties</h2>
                <ul className="list-disc pl-6 space-y-2 text-neutral-700 text-base leading-relaxed marker:text-[#001588]">
                  {d.signature_properties.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
            {d.best_suited_for && (
              <div className="mt-12" data-testid="dev-best-suited">
                <h2 className="font-serif text-3xl lg:text-4xl text-[#050d19] mb-5">Best suited for</h2>
                <p className="text-neutral-700 leading-relaxed text-lg whitespace-pre-line">{d.best_suited_for}</p>
              </div>
            )}
            {d.not_ideal_for && (
              <div className="mt-12" data-testid="dev-not-ideal">
                <h2 className="font-serif text-3xl lg:text-4xl text-[#050d19] mb-5">May not be ideal for</h2>
                <p className="text-neutral-700 leading-relaxed text-lg whitespace-pre-line">{d.not_ideal_for}</p>
              </div>
            )}
            {d.verdict && (
              <div className="mt-12 border-l-4 border-[#001588] pl-6 py-4 bg-[#001588]/[0.03]" data-testid="dev-verdict">
                <h2 className="font-serif text-3xl lg:text-4xl text-[#001588] mb-4">Point Penta verdict</h2>
                <p className="text-neutral-800 leading-relaxed font-serif text-xl italic whitespace-pre-line">{d.verdict}</p>
              </div>
            )}
            {d.website && <a href={d.website} target="_blank" rel="noopener noreferrer" className="inline-block mt-10 text-sm text-[#001588] hover:underline" data-testid="dev-website">Visit developer website →</a>}
          </div>
          <aside className="border border-neutral-200 p-5 space-y-3 text-sm h-fit">
            {d.headquarters && <div><div className="overline text-neutral-500 mb-1">Headquarters</div><div>{d.headquarters}</div></div>}
            {founded && <div><div className="overline text-neutral-500 mb-1">Established</div><div>{founded}</div></div>}
            {(d.emirates_of_operation?.length > 0) && (
              <div>
                <div className="overline text-neutral-500 mb-1">Emirates of operation</div>
                <div>{d.emirates_of_operation.join(", ")}</div>
              </div>
            )}
            {d.website && (
              <div>
                <div className="overline text-neutral-500 mb-1">Website</div>
                <a href={d.website} target="_blank" rel="noopener noreferrer" className="text-[#001588] hover:underline break-all">{d.website.replace(/^https?:\/\//, "")}</a>
              </div>
            )}
          </aside>
        </div>
      </section>
      {d.projects?.length > 0 && (
        <section className="py-14 border-b border-neutral-200">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <h2 className="font-serif text-4xl mb-8">Signature projects</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {d.projects.map((p) => (
                <Link key={p.slug} to={`/projects/${p.slug}`} className="group block border border-neutral-200 hover:border-[#001588] transition-colors" data-testid={`dev-project-${p.slug}`}>
                  <div className="pp-hover-zoom aspect-[4/3]">
                    {p.hero_image && <img src={p.hero_image} alt={p.name} className="w-full h-full object-cover" loading="lazy" />}
                  </div>
                  <div className="p-5">
                    <div className="overline text-[#001588] mb-1">{p.status}</div>
                    <h3 className="font-serif text-xl group-hover:text-[#001588]">{p.name}</h3>
                    {p.starting_price && <div className="text-xs text-neutral-500 mt-2">From AED {Number(p.starting_price).toLocaleString()} · {p.community}</div>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
      {d.listings?.length > 0 && (
        <section className="py-14">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <h2 className="font-serif text-4xl mb-8">Available mandates by {d.name}</h2>
            <div className="grid md:grid-cols-3 gap-x-8 gap-y-14">
              {d.listings.map((p) => <PropertyCard key={p.id} p={p} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
